import {
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { RequestError, Store, ValidationStore } from '@softline/core';
import { Ticket } from '../../data/ticket.model';
import { NewTicketDocument } from '../../data/document.model';
import {
  IconComponent,
  MessageBarService,
  modalFn,
  translator,
  Validators,
} from '@softline/ui-core';
import { EditContactsDialogComponent } from '../../dialogs/edit-contacts-dialog/edit-contacts-dialog.component';
import { TicketContact, TicketContactType } from '../../data/contact.model';
import {
  backNavigation,
  BackNavigationService,
  handleRequestError,
  title,
} from '@softline/application';
import {
  SOFTLINE_FEATURE_TICKET,
  SOFTLINE_FEATURE_TICKET_DOCUMENT,
  SOFTLINE_FEATURE_TICKET_HOTLINE,
  SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
  TicketHotlineStore,
  TicketTypeStore,
} from '../../ticket.shared';
import { TicketDocumentsStore } from '../../store/document.store';
import { ActivatedRoute, Router } from '@angular/router';
import { UserInfoService } from '@softline/auth';
import { SoftAppsUserInfo } from '@softapps/core';
import { CommonModule } from '@angular/common';
import { ContactTypePipe } from '../../pipes/contact-type.pipe';

import { ChipComponent } from '@softline/ui-core';
import { FileInputComponent } from '@softline/ui-core';
import { GroupByPipe } from '@softline/ui-core';
import { MultilineInputComponent } from '@softline/ui-core';
import { RepeatDirective } from '@softline/ui-core';
import { SelectComponent, SelectOptionDirective, SelectOptionHeaderDirective, SelectOptionSeparatorDirective } from '@softline/ui-core/combo-box';
import { ToArrayPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { ValidationContainerComponent } from '@softline/ui-core';
import { ValidationMessagePipe } from '@softline/ui-core';
import { InputComponent } from '@softline/ui-core';
import { TicketService } from '../../services/ticket.service';
import { ToolTipDirective } from '@softline/ui-core';
import { toSignal } from '@angular/core/rxjs-interop';
@Component({
  selector: 'soft-create-ticket',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ContactTypePipe,
    ChipComponent,
    FileInputComponent,
    GroupByPipe,
    MultilineInputComponent,
    RepeatDirective,
    SelectComponent,
    SelectOptionDirective,
    ToArrayPipe,
    TranslatePipe,
    ValidationContainerComponent,
    ValidationMessagePipe,
    InputComponent,
    SelectOptionHeaderDirective,
    SelectOptionSeparatorDirective,
    IconComponent,
    ToolTipDirective,
  ],
  templateUrl: './create-ticket.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-ticket.component.css'],
})
export class CreateTicketComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly element = inject(ElementRef);
  protected readonly router = inject(Router);
  protected readonly backNavigationService = inject(BackNavigationService);
  private readonly ticketService = inject(TicketService);
  private readonly messageBarService = inject(MessageBarService);
  private readonly injector = inject(Injector);
  private route = inject(ActivatedRoute);
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);

  private readonly modal = modalFn();

  private readonly t = translator();

  validationSubscription?: Subscription;

  uploadState$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET_DOCUMENT,
    TicketDocumentsStore.getters.uploadProgress
  );
  validation$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET,
    ValidationStore.getters.validation.validation
  );
  hotlines$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET_HOTLINE,
    TicketHotlineStore.getters.all
  );
  ticketTypes$ = this.store.observe(
    SOFTLINE_FEATURE_TICKET_TICKET_TYPE,
    TicketTypeStore.getters.all
  );

  private resolverData = toSignal(this.route.data);
  defaultFormValues = computed(
    () => this.resolverData()?.['defaultFormValues'] as Ticket
  );

  public readonly loading = this.ticketService.isLoading;

  form = new UntypedFormGroup(
    {
      name: new UntypedFormControl(null, Validators.required()),
      description: new UntypedFormControl(null, Validators.required()),
      type: new UntypedFormControl(null, Validators.required()),
      priority: new UntypedFormControl(5, Validators.required()),
      contacts: new UntypedFormControl([]),
      hotline: new UntypedFormControl(null),
      files: new UntypedFormControl(null),
    },
    {
      asyncValidators: (control) => this.validateForm(this.store, control),
      updateOn: 'blur',
    }
  );

  constructor() {
    backNavigation('/ticket');
    title(this.t('#TICKETS.CREATE.TITLE'));
  }

  async ngOnInit(): Promise<void> {
    this.validationSubscription = this.form.valueChanges
      .pipe(
        debounceTime(100),
        map((entity) => ({
          ...entity,
          contacts: entity.hotline
            ? [
                ...(entity.contacts ?? []),
                { type: TicketContactType.Responsible, contact: this.form.value.hotline },
              ]
            : (entity.contacts ?? []),
          hotline: undefined,
          files: undefined,
        }))
      )
      .subscribe((entity) =>
        this.store.dispatch(
          SOFTLINE_FEATURE_TICKET,
          ValidationStore.actions.validation.validate,
          { entity }
        )
      );
  }

  ngOnDestroy(): void {
    if (this.validationSubscription && !this.validationSubscription.closed)
      this.validationSubscription.unsubscribe();
    this.validationSubscription = undefined;
  }

  protected validateForm(store: Store, control: AbstractControl): Observable<any> {
    return store
      .observe(SOFTLINE_FEATURE_TICKET, ValidationStore.getters.validation.validations)
      .pipe(map((o) => ({ isValid: o?.every((prop) => prop.isValid) })));
  }

  async onSubmit(): Promise<void> {
    let contacts = this.form.value.contacts ?? [];

    if (this.form.value.hotline) {
      contacts = [
        ...contacts,
        { type: TicketContactType.Responsible, contact: this.form.value.hotline },
      ];
    }

    const ticket = {
      name: this.form.value.name,
      description: this.form.value.description,
      type: this.form.value.type,
      priority: this.form.value.priority,
      customer: { id: this.userInfo.value()?.accountid },
      contacts,
    };
    const def = this.defaultFormValues();

    const entity = ticket as unknown as Ticket;

    const createdId = await this.ticketService.createTicket(entity);

    if (!createdId) {
      this.messageBarService.error(this.t(`#TICKETS.MESSAGES.CREATE_TICKET.FAILURE`));
      return;
    }

    if (!this.form.value.files || this.form.value.files.length < 1) {
      this.form.reset(def);
      return;
    }

    try {
      const documents: NewTicketDocument[] = this.form.value.files.map((file) => ({
        ticketId: createdId,
        files: [file],
      }));

      for (const document of documents) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_TICKET_DOCUMENT,
          TicketDocumentsStore.actions.upload,
          { entity: document }
        );
      }

      this.messageBarService.success(this.t('#TICKETS.MESSAGES.GENERAL.SUCCESS_UPLOAD'));
    } catch (e) {
      if (e instanceof RequestError) {
        this.messageBarService.error(this.t('#TICKETS.MESSAGES.GENERAL.ERROR_UPLOAD'));
      } else {
        handleRequestError(e, this.injector, { show: true });
      }
    }

    this.form.reset(def);
    this.scrollToTop(this.element.nativeElement);
  }

  async changeContact(selected: TicketContact[]): Promise<void> {
    const result = await this.modal({
      id: 'CHANGE_OBSERVER',
      component: EditContactsDialogComponent,
      title: `Teilnehmer bearbeiten`,
      data: {
        selectedI: selected ?? [],
        contactTypesI: [TicketContactType.Observer],
      },
      dismiss: true,
    });
    if (result !== 'DISMISSED') {
      this.form.patchValue({ contacts: result });
    }
  }

  removeContact(contact: TicketContact): void {
    const contacts = [...this.form.value.contacts] as TicketContact[];
    const index = contacts.findIndex(
      (o) => o.type === contact.type && o.contact.id === contact.contact.id
    );
    contacts.splice(index, 1);
    this.form.patchValue({ contacts });
  }

  scrollToTop(node: any): void {
    if (node == null) {
      return;
    }
    while (node.scrollHeight <= node.clientHeight) {
      node = node.parentNode;
    }
    if (typeof node.scrollTo === 'function') {
      node.scrollTo(0, 0);
    }
  }
}
